/**
 * 명함 사진 한 장 — 있으면 사진, 없으면 이니셜 (2026-10-06 사용자 지시 3·4·5).
 *
 * ## ⭐ 꼴을 «빌렸다» — 새로 짓지 않았다
 *
 * 파일 번호를 그림으로 바꾸는 길은 이 레포에 이미 셋이 있고 전부 같은 모양이다:
 * ```
 * BoardsPage/wide/BoardThumb.tsx      목록 썸네일 ← ★ 이 판과 «같은 자리»(표 안 N줄)
 * MailPage/MailAttachThumb.tsx        첨부 썸네일
 * ChatPage/ChatArea/ChatMediaPanel    미디어 패널
 * ```
 * ⇒ `requestPresignedDownload(fileId, { purpose: 'preview' })` → `<img loading="lazy">` 이고,
 *   주소가 오기 «전»과 «실패한 뒤»에는 자리를 유지한 채 폴백을 둔다.
 *
 * ⛔ **`lib/avatar-src.ts` 의 센티널 해석은 빌리지 «않았다».** 그 쪽은 `wcavatar:{ts}` 꼴만
 *    알아듣고 **맨 파일 번호에는 `null` 을 돌려준다**(`WC_RE` 가 안 맞고 http 도 아니라서).
 *    `photoFileId` 는 맨 파일 번호이므로 그 길로 가면 **사진이 영영 안 뜬다.**
 *    ⚠ 2026-10-06 에 내가 `contacts-i18n` 머리말에 「센티널 해석을 빌리라」고 적었던 것은
 *      **틀렸고**, 이 판에서 그 줄을 고쳤다. (그 파일은 사람 아바타용이고 `userId` 를 받는다 —
 *      명함첩의 한 장은 우리 사용자가 아니라 외부 주소라서 애초에 대상이 아니다.)
 *
 * ⚠ `disposition` 은 **안 준다.** `BoardThumb` 은 `'inline'` 을 주는데, 그 칸의 주석이
 *   「`<audio>`/`<video src>` 스트리밍용 — MinIO Range(206) 로 seek」라고 적어 둔 길이다.
 *   `<img>` 는 seek 가 없으니 기본값으로 둔다 — `MailAttachThumb` 과 같다.
 *   (`purpose: 'preview'` 는 감사 이름표만 가른다. 썸네일은 「열람」이다 — 계약 §3.4.1.)
 *
 * ## ⭐ 사진이 «없을 때»는 오늘 화면과 글자 하나까지 같다
 *
 * `photoFileId` 는 **선택 칸**이다(아래 `api/contacts.ts` 참조). 옛 서버는 그 칸을 아예 안 주므로
 * `undefined` 가 오고, 그때 그리는 것은 **종전 그대로의 이니셜 동그라미**다. ⇒ 서버가 아직
 * 안 올라간 동안 이 판이 main 에 들어가도 화면은 안 바뀐다.
 *
 * ## ⭐ 2026-10-06 사용자 결정 — 「파일을 선택해도 «저장하지 않으면» 올라가면 안된다」
 *
 * 그래서 고른 순간에는 서버에 아무것도 없다. 그 동안의 그림은 `previewUrl`(로컬 objectURL)이
 * 맡고, **서버 주소를 이긴다.** 올리기는 「담기」를 누를 때 `ContactEditModal` 이 한다.
 *
 * ⚠ 내가 #2212 에서 「저장 때로 미루면 미리보기가 없어진다」고 적었던 것은 **틀렸다.**
 *   `URL.createObjectURL` 로 **올리지 않고도** 그릴 수 있고, 그 꼴이 이 레포에 이미 있었다 —
 *   `components/AvatarUploader/AvatarUploader.tsx:44·56·57·74`(`localPreview`).
 *   ⇒ 미리보기와 「고아 파일 0」을 **둘 다** 얻는다. 둘 중 하나를 고를 일이 아니었다.
 *
 * ## ⭐⭐ 2026-10-07 사용자 결정 — **동그라미가 아니라 «직사각 명함»이다**
 *
 * 운영에서 보고 「프로필 위치에 명함 이미지(**직사각형**)로 보여 달라」고 하셨다. 시안
 * (5테마 × 라이트/다크 · 갈래 넷)을 보신 뒤 **①ⓒ ②ⓒ ③ⓑ ④ⓑ** 로 정하셨다:
 * ```
 * ①ⓒ 목록 72×40          (명함비 9:5 · 높이 40)
 * ②ⓒ 수정 창 198×110
 * ③ⓑ object-fit: contain  ⇒ ⛔ cover 로 «자르지 않는다» — 명함에는 글자가 있다
 * ④ⓑ 사진이 «없는» 줄도 ★ 「빈 명함」 자리 — 같은 틀에 이니셜만
 * ```
 *
 * ⛔⛔ **④ⓑ 는 종전 지시를 «덮는다».** 관제가 앞서 「사진이 없을 때는 종전 이니셜 동그라미
 *     그대로 — **회귀 금지**」라고 주었는데, **이 사용자 결정이 그것을 덮었다.**
 *     ⇒ ★ 그러므로 이제 **동그라미로 돌아가는 것이 «회귀 방지»가 아니라 «결정 위반»**이다.
 *     ⇒ ⛔ 되돌리려면 **새 사용자 결정**이 필요하다.
 *
 * ⚠ **「줄이 높아진다」는 말이 나오면** — 그것은 결함이 아니다. 사용자가 시안의 「결정 확인」
 *   장(같은 높이 창 둘 · 보이는 줄 수를 화면에서 «센» 것)을 **보고 나서** 「그대로 진행」이라
 *   답했다. ⇒ 즉 **그 대가를 알고 받아들였다.**
 *   ⇒ ★ 되돌릴 길은 이미 재어 두었다: **① 만 ⓑ(50×28)로 내리면 ②③④ 는 그대로 둘 수 있다.**
 *     ⛔ 다만 사용자가 그 길을 보고도 「그대로」라 했으므로 **쓰지 않는다** — 새 결정이 필요하다.
 *
 * ⭐ 그리고 **실측이 시안보다 나았다**(짓고 나서 쟀다): 목록 줄 높이는 내용이 아니라
 *   `components/shared/Table/Table.css:35` 의 `height: var(--h-xl)`(**48px**)로 **못 박혀** 있고,
 *   `data-density` 를 거는 곳은 `MailPage` 뿐이다(명함첩은 기본 밀도). ⇒ ★ **40px 카드는 48px
 *   줄에 들어가므로 «줄 수가 안 줄어든다».** 시안의 그 대가는 내 흉내(내용으로 늘어나는 줄)의
 *   것이었다 — 실제로는 치르지 않는다. 그 어긋남을 PR 에 적었다.
 *
 * ## ✅ 2026-10-07 — **사용자 육안 확인 완료.** 그 전에 무엇을 «못 봤다»고 적었나
 *
 * #2238 을 올릴 때 PR 본문의 「미검증」에 이렇게 적었다(⚠ 그 줄은 **소스가 아니라 PR 에** 있었다):
 * ```
 * ⛔ 눈으로 못 봤다 — jsdom 에는 레이아웃이 없어 「72×40 으로 보이나」·「글자가 읽히나」를
 *    그려서는 못 잰다
 * ⛔ 운영에 사진 붙은 명함이 거의 없어 «실제 사진으로 contain 을 본 적이 없다»
 * ```
 * ⇒ ★ 관제가 그것을 **31차 지시서의 「미검증」 절에 그대로** 실어 보냈고, **사용자가 보고
 *   닫았다**(31차 종결 · 기준 `7c08cdbad`). ⇒ ⭐ **그 둘이 닫혔다.**
 *
 * ⚠ **그래도 시험이 재는 것은 바뀌지 않았다.** 사용자가 본 것은 **한 번**이고, 자물쇠는 여전히
 *   ⓐ **CSS 글자**(`ContactsPage.css.test.ts` — 40px · 9/5 · contain · 110px)와
 *   ⓑ **붙는 class**(이 파일의 행동 시험)다.
 *   ⇒ ⛔ 「`aspect-ratio` 가 실제로 72px 를 낸다」는 여전히 **산수**(40 × 9/5)뿐이다 — 그 줄은
 *     고치지 않았다. 육안 확인이 **시험을 대신하지 않는다.**
 *
 * ## ⛔ 2026-10-07 사용자 결정 — **직원 카드에는 명함 사진을 보이지 않는다**
 *
 * 「대량 조회(직원 카드 · `components/PersonCard`)에 명함 사진을 보일지」를 올렸고 **「안보인다」**로
 * 답이 왔다. ⇒ ⛔ **넣지 않는다.** 넣으려면 **새 사용자 결정**이 필요하다.
 * ⇒ ⚠ **까닭은 적지 않는다** — 사용자가 한 마디로 답했고, 까닭을 지어내면 틀린다.
 *
 * ⚠ 그리고 **이 줄은 뒤집힐 수 있다**(지금 아는 것을 그대로 적는다): 관제가 그 물음을 **다른
 *   하나와 함께** 올렸고(워크스페이스 휴지통 실증), 답이 「안보인다」 **하나**라 **어느 쪽이었는지
 *   가리지 못했다.** ⇒ 관제가 그 다른 쪽이었을 경우 **다시 여쭙기로** 했다.
 *   ⇒ ★ 어느 쪽이든 **지금은 안 보이는 쪽**이 맞다(서버가 대량 조회에서 그 칸을 안 보낸다).
 */

import { useEffect, useState } from 'react';
import { requestPresignedDownload } from '../../api/files';

export interface ContactPhotoProps {
  /** 서버가 준 파일 번호. 없거나 null 이면 이니셜만 그린다. */
  photoFileId?: string | null;
  /**
   * 아직 **안 올린** 파일의 로컬 미리보기(`URL.createObjectURL`). 주면 **이것이 이긴다.**
   *
   * ★ 2026-10-06 사용자 결정 — 「파일을 선택해도 저장하지 않으면 올라가면 안된다」.
   *   그래서 고른 순간에는 **서버에 아무것도 없다.** 미리보기는 브라우저가 로컬 파일을
   *   그대로 그려 준다. ⇒ 이 값이 있으면 presigned 를 **부르지 않는다**(부를 번호가 없다).
   * ⚠ 만드는·치우는 책임은 **주는 쪽**이다(`revokeObjectURL`). 여기서는 그리기만 한다 —
   *   이 부품이 치우면 부모가 아직 쓰고 있는 주소를 끊는다.
   */
  previewUrl?: string | null;
  /** 이니셜을 뽑을 이름. 비어 있으면 「?」 — 종전 규칙 그대로. */
  name: string | null;
  /** 명함 틀 크기 — `row` = 목록 72×40 · `lg` = 수정 창 198×110 (2026-10-07 결정 ①ⓒ②ⓒ). */
  variant?: 'row' | 'lg';
}

export function ContactPhoto({ photoFileId, previewUrl, name, variant = 'row' }: ContactPhotoProps) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    // 번호가 없으면 **부르지 않는다** — 목록 N줄이 빈 요청을 N번 보내면 안 된다.
    // 로컬 미리보기가 있으면 **그것도 안 부른다** — 아직 서버에 없는 파일이다.
    setUrl(null);
    if (previewUrl || !photoFileId) return;
    let cancelled = false;
    requestPresignedDownload(photoFileId, { purpose: 'preview' })
      .then((res) => { if (!cancelled) setUrl(res.downloadUrl); })
      // 못 받으면 이니셜로 떨어진다 — 깨진 그림 자리를 남기지 않는다(BoardThumb 과 같은 규율).
      .catch(() => { /* 조용히 폴백 */ });
    return () => { cancelled = true; };
  }, [photoFileId, previewUrl]);

  const trimmed = name?.trim() ?? '';
  const cls = variant === 'lg' ? 'ctp-card ctp-card--lg' : 'ctp-card';
  // 로컬 미리보기가 서버 주소를 **이긴다** — 사용자가 방금 고른 것이 지금 참이다.
  const shown = previewUrl ?? url;

  return (
    <span className={cls} aria-hidden>
      {shown
        // 받아 둔 주소가 403·만료로 깨지면 그때도 «빈 명함»으로 떨어진다.
        ? <img src={shown} alt="" loading="lazy" onError={() => setUrl(null)} />
        /*
         * ⭐ 사진이 없으면 **«빈 명함» 자리**다 — 같은 직사각 틀에 이니셜만 넣는다
         *   (2026-10-07 사용자 결정 ④ⓑ). ⛔ 동그라미로 돌아가지 않는다.
         */
        : <span className="ctp-card__ini">{trimmed ? trimmed.slice(0, 1) : '?'}</span>}
    </span>
  );
}
